/*
  useIntegrationStatus() / useFeature(key) — is an outside-service feature set up and switched on?

    const { isEnabled, feature, loading, error, refresh } = useIntegrationStatus();
    if (isEnabled('email_outbound')) …                     // false while loading and when anything failed
    const sso = useFeature('microsoft_sso');               // { key, label, status, enabled, configured, loading, error … }

  One request per session for every component (shared cache, 5 min), GET /api/integrations/status.
  Admin › Integrations calls `refresh()` after switching a feature. Not signed in (no token) → no request,
  everything reads "not configured" (the sign-in page uses `loadSignInOptions()` from lib/integrations).
  The cache belongs to the token it was fetched with: after a sign-out and a different sign-in in the same tab
  (AuthContext does not reload the page) the previous person's statuses — for an Admin, including the
  checklist fields — are never shown; the new session fetches its own.
  Statuses: 'on' | 'off' (set up, switched off) | 'not_configured' (IT has to set it up). While loading, and
  when the request failed, `feature(key)` is a 'not_configured' placeholder with `placeholder: true` — check
  `loading` / `error` before telling anyone a feature is "not set up" (NotConfigured does).
*/
import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import api from '@/lib/api';
import { FEATURE_STATUS, featureFromStatus } from '@/lib/integrations';

const MAX_AGE_MS = 5 * 60 * 1000;

const EMPTY = { data: null, error: null, loading: false, fetchedAt: 0, fetchedFor: '' };
let store = EMPTY;
let inflight = null; // { token, promise }
const listeners = new Set();

const setStore = (patch) => {
  store = { ...store, ...patch };
  listeners.forEach((listener) => listener());
};

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getSnapshot = () => store;

const currentToken = () => {
  try {
    return window.localStorage.getItem('token') || '';
  } catch {
    return '';
  }
};

/** Fetch (or reuse) the status. Resolves with the response data, or null when it could not be loaded. */
export function loadIntegrationStatus({ force = false } = {}) {
  const token = currentToken();
  if (!token) return Promise.resolve(null);
  if (inflight && inflight.token === token) return inflight.promise;
  const sameSession = store.fetchedFor === token;
  if (!force && sameSession && store.data && Date.now() - store.fetchedAt < MAX_AGE_MS) return Promise.resolve(store.data);
  // Another session's statuses are dropped before anything is fetched for this one.
  setStore(sameSession ? { loading: true } : { ...EMPTY, loading: true, fetchedFor: token });
  const request = api.get('/integrations/status')
    .then((res) => {
      if (currentToken() !== token) return null; // signed out (or someone else signed in) meanwhile
      setStore({ data: res.data, error: null, loading: false, fetchedAt: Date.now(), fetchedFor: token });
      return res.data;
    })
    .catch((error) => {
      if (currentToken() === token) setStore({ error, loading: false, fetchedFor: token });
      return null;
    });
  const entry = {
    token,
    promise: request.finally(() => {
      if (inflight === entry) inflight = null;
    }),
  };
  inflight = entry;
  return entry.promise;
}

/** Forget the cached status (tests, sign-out). */
export function resetIntegrationStatus() {
  inflight = null;
  setStore(EMPTY);
}

export function useIntegrationStatus() {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const token = currentToken();
  const sameSession = Boolean(token) && snapshot.fetchedFor === token;
  const data = sameSession ? snapshot.data : null;
  const error = sameSession ? snapshot.error : null;

  useEffect(() => {
    loadIntegrationStatus();
  }, [token]);

  const refresh = useCallback(() => loadIntegrationStatus({ force: true }), []);
  const feature = useCallback((key) => featureFromStatus(data, key), [data]);
  const isEnabled = useCallback((key) => featureFromStatus(data, key).status === FEATURE_STATUS.ON, [data]);
  const loading = Boolean(token) && ((sameSession && snapshot.loading) || (!data && !error));

  return useMemo(() => ({
    status: data,
    features: data?.features || {},
    loading,
    error,
    refresh,
    feature,
    isEnabled,
  }), [data, loading, error, refresh, feature, isEnabled]);
}

export function useFeature(key) {
  const { feature, loading, error, refresh } = useIntegrationStatus();
  const info = feature(key);
  return { ...info, enabled: info.status === FEATURE_STATUS.ON, loading, error, refresh };
}

export default useIntegrationStatus;
